<script setup lang="ts">
import { ref, watch } from 'vue';
import { ReviewRequestSchema, UiAside, UiButton, UiText, UiTextarea, UiIcon, UiAvatar, generateInitials } from '@shopexperts/shared';
import { z } from 'zod';
import { useSendReminder } from '../../composables/reviews/useSendReminder';
import SuccessCheckmarkIcon from '../../../assets/icons/success-chekmark.svg';

type ReviewRequest = z.infer<typeof ReviewRequestSchema>;

const asideOpen = defineModel<boolean>('open');

const props = defineProps<{
  request: ReviewRequest | null;
}>();

const message = ref('');
const showSuccess = ref(false);
const { sendReminder, isSubmitting } = useSendReminder();

watch(asideOpen, (isOpen) => {
  if (!isOpen) {
    setTimeout(() => {
      message.value = '';
      showSuccess.value = false;
    }, 300);
  }
});

const onCancel = () => {
  asideOpen.value = false;
};

const handleSubmit = async () => {
  if (!props.request) return;

  try {
    const trimmed = message.value.trim() || undefined;
    await sendReminder(props.request.id, trimmed);
    showSuccess.value = true;
  } catch {
    // Error is handled in the composable
  }
};
</script>

<template>
  <UiAside v-model="asideOpen" position="right" :close-on-overlay-click="false">
    <div class="px-4 py-6 md:p-8 flex flex-col gap-4 h-full overflow-y-auto">
      <!-- Header -->
      <div class="flex items-center justify-between">
        <UiText as="h3" variant="h3">
          <span class="font-normal">Send a </span>
          <span class="font-besley italic">reminder</span>
        </UiText>
        <UiButton variant="ghost" shape="square" @click="onCancel">
          <template #before_label>
            <UiIcon name="close" class="w-6 h-6" />
          </template>
        </UiButton>
      </div>

      <!-- Form State -->
      <template v-if="!showSuccess && props.request">
        <UiText
          as="p"
          variant="p"
          class="text-se-gray-600"
          text="Send a one-time follow-up reminder to your client. You can include an optional personalized message."
        />

        <!-- Client context -->
        <div class="flex items-center gap-3 p-3 bg-se-gray-50 rounded-lg">
          <UiAvatar
            :initials="generateInitials(props.request.client_name || 'Client')"
            size="xs"
          />
          <div class="min-w-0">
            <UiText
              as="p"
              variant="p-small"
              class="font-semibold text-se-gray-900 truncate"
              :text="props.request.client_name || 'Unknown Client'"
            />
            <UiText
              v-if="props.request.client_company"
              as="p"
              variant="p-small"
              class="text-se-gray-600 truncate"
              :text="props.request.client_company"
            />
          </div>
        </div>

        <!-- Message -->
        <div class="flex flex-col gap-2">
          <UiText
            as="label"
            variant="p-small"
            class="font-medium text-se-gray-700"
            text="Custom message (optional)"
          />
          <UiTextarea
            v-model="message"
            :rows="5"
            placeholder="Just checking in - I would really appreciate your feedback when you have a moment."
            class="w-full"
          />
          <UiText
            as="p"
            variant="p-small"
            class="text-se-gray-500"
            text="This message will be included in the reminder email alongside the review link."
          />
        </div>

        <!-- Actions -->
        <div class="flex justify-end gap-3 pt-2">
          <UiButton
            variant="outline"
            label="Cancel"
            @click="onCancel"
          />
          <UiButton
            variant="primary"
            label="Send Reminder"
            :disabled="isSubmitting"
            @click="handleSubmit"
          >
            <template #before_label>
              <UiIcon name="arrow-send" class="w-4 h-4" />
            </template>
          </UiButton>
        </div>
      </template>

      <!-- Success State -->
      <div
        v-if="showSuccess"
        class="flex flex-col items-center justify-center gap-6 py-12 text-center h-full"
      >
        <div class="w-20 h-20">
          <SuccessCheckmarkIcon class="w-full h-full" />
        </div>

        <div class="flex flex-col gap-2">
          <UiText as="h4" variant="h4" class="font-semibold text-se-gray-900" text="Reminder sent!" />
          <UiText
            as="p"
            variant="p"
            class="text-se-gray-600"
            text="Your client will receive a follow-up email with the review link. This is a one-time reminder per request."
          />
        </div>

        <UiButton
          variant="outline"
          label="Close"
          @click="onCancel"
        />
      </div>
    </div>
  </UiAside>
</template>
